import type { QueryClient } from "@tanstack/react-query";

import {
  invalidateDashboardAffectedQueries,
  isDashboardAffectedQueryKey,
} from "@/lib/query/dashboard-affected-queries";

/**
 * Request-path fragments for the resources a follow-up-action write can change
 * beyond the dashboard queries, which `dashboard-affected-queries` always adds.
 *
 * One fragment covers a lot: the nursery list feeding Task Manager and the
 * Follow-up Actions page, the per-checklist list, and the action's own detail
 * view all sit under `/follow-up-actions`.
 *
 * The dashboard queries are the load-bearing half. `awaitingSignOff` — the flag
 * that renders the sign-off prompt on the priority list, and the manager's only
 * route to signing the action off — is carried by the dashboard status payload
 * alone; `FollowUpActionResponseDto` has no such field. So invalidating the
 * action lists without the dashboard leaves the manager unable to sign off until
 * a manual refresh.
 */
const FOLLOW_UP_ACTION_AFFECTED_KEY_PATHS = ["/follow-up-actions"] as const;

/**
 * True when a React Query key belongs to a resource that a follow-up-action
 * write can change. Exported for the drift-guard test.
 */
export function isFollowUpActionAffectedQueryKey(
  queryKey: readonly unknown[],
): boolean {
  return isDashboardAffectedQueryKey(
    queryKey,
    FOLLOW_UP_ACTION_AFFECTED_KEY_PATHS,
  );
}

/**
 * Invalidate every query that renders data a follow-up-action write can change.
 */
export async function invalidateFollowUpActionQueries(
  queryClient: QueryClient,
): Promise<void> {
  await invalidateDashboardAffectedQueries(queryClient, {
    keyPaths: FOLLOW_UP_ACTION_AFFECTED_KEY_PATHS,
  });
}
